import { useCallback, useEffect, useRef, useState } from "react";

const AT_BOTTOM_THRESHOLD_PX = 120;

export function useAutoScroll(deps: ReadonlyArray<unknown>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastScrollHeightRef = useRef(0);

  const checkIsAtBottom = useCallback((): boolean => {
    const el = containerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onScroll() {
      setIsAtBottom(checkIsAtBottom());
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [checkIsAtBottom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const lastHeight = lastScrollHeightRef.current;
    const hasGrown = el.scrollHeight > lastHeight && lastHeight > 0;
    const overflows = el.scrollHeight > el.clientHeight;
    if (hasGrown && overflows && isAtBottom) {
      el.scrollTop = el.scrollHeight;
    }
    lastScrollHeightRef.current = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, isAtBottom]);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setIsAtBottom(true);
  }, []);

  return { containerRef, isAtBottom, scrollToBottom };
}
